/**
 * Authentication API endpoints
 */
import { api } from './baseApi';

interface LoginRequest {
  phone_number: string;
  password: string;
}

interface LoginResponse {
  access: string;
  refresh: string;
}

interface Role {
  id: number;
  name: string;
  code: string;
}

interface EmployeeProfile {
  id: number;
  employee_id: string;
  role: Role | null;
  department: string | null;
  position: string | null;
  status: 'active' | 'inactive' | 'suspended';
  is_active: boolean;
  can_access_dashboard: boolean;
  permissions: string[];
  /** When true, only custom_permissions (+ deps) apply; role codes are not used for access. */
  use_only_custom_permissions?: boolean;
  /** M2M extras on the employee; merged with role unless use_only_custom_permissions. */
  custom_permissions?: string[];
  role_permissions?: string[];
  permission_breakdown?: {
    from_role: string[];
    extra_on_employee_record: string[];
    effective: string[];
    role_included_in_effective?: boolean;
  };
}

interface UserResponse {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  profile_picture: string | null;
  is_staff: boolean;
  is_superuser: boolean;
  is_verified: boolean;
  employee_profile?: EmployeeProfile | null;
}

interface RefreshRequest {
  refresh: string;
}

interface RefreshResponse {
  access: string;
}

interface LogoutRequest {
  refresh_token?: string;
}

interface ForgotPasswordRequest {
  email: string;
}

interface ForgotPasswordResponse {
  success: boolean;
  message: string;
  data: {
    otp?: string;
  };
}

interface VerifyResetOTPRequest {
  email: string;
  otp: string;
}

interface VerifyResetOTPResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
  };
}

interface ResetPasswordRequest {
  token: string;
  new_password: string;
  confirm_password: string;
}

interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/users/login/',
        method: 'POST',
        body: credentials,
      }),
    }),
    refreshToken: builder.mutation<RefreshResponse, RefreshRequest>({
      query: (body) => ({
        url: '/users/token/refresh/',
        method: 'POST',
        body,
      }),
    }),
    logout: builder.mutation<void, LogoutRequest | void>({
      query: (body) => ({
        url: '/users/logout/',
        method: 'POST',
        body: body || {},
      }),
    }),
    getMe: builder.query<UserResponse, void>({
      query: () => '/users/profile/',
      providesTags: ['User'],
    }),
    forgotPassword: builder.mutation<ForgotPasswordResponse, ForgotPasswordRequest>({
      query: (body) => ({
        url: '/users/password/forgot/',
        method: 'POST',
        body,
      }),
    }),
    verifyResetOTP: builder.mutation<VerifyResetOTPResponse, VerifyResetOTPRequest>({
      query: (body) => ({
        url: '/users/password/verify-otp/',
        method: 'POST',
        body,
      }),
    }),
    resetPassword: builder.mutation<ResetPasswordResponse, ResetPasswordRequest>({
      query: (body) => ({
        url: '/users/password/reset/',
        method: 'POST',
        body,
      }),
    }),
    resendResetOTP: builder.mutation<ForgotPasswordResponse, ForgotPasswordRequest>({
      query: (body) => ({
        url: '/users/password/resend-otp/',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useRefreshTokenMutation,
  useLogoutMutation,
  useGetMeQuery,
  useForgotPasswordMutation,
  useVerifyResetOTPMutation,
  useResetPasswordMutation,
  useResendResetOTPMutation,
} = authApi;

